import type { BrowserOptions, Breadcrumb, Event, EventHint } from '@sentry/vue';
import type { App } from 'vue';
import * as Sentry from '@sentry/vue';
import { isAxiosError } from 'axios';
import { watch } from 'vue';
import { getCspNonce } from '@/helpers/cspNonce';
import { redactPlatformOauthSecrets, redactPlatformOauthValues } from '@/helpers/platformOauthTelemetry';
import { useNarrative } from './useNarrative';

// Standard GraphQL validation/syntax codes indicate a malformed query shipped from the
// frontend (a genuine bug), as opposed to a backend-authored business message. These must
// keep reaching Sentry. PREPBUSINESS-ABM
const developerGraphQLErrorCodes = new Set(['GRAPHQL_VALIDATION_FAILED', 'GRAPHQL_PARSE_FAILED']);

function isDeveloperGraphQLError(error: unknown): boolean {
    if (error == null || typeof error !== 'object' || !('extensions' in error)) {
        return false;
    }

    const { extensions } = error;
    if (extensions == null || typeof extensions !== 'object' || !('code' in extensions)) {
        return false;
    }

    return typeof extensions.code === 'string' && developerGraphQLErrorCodes.has(extensions.code);
}

function isGraphQLBusinessError(exception: unknown): boolean {
    if (exception == null || typeof exception !== 'object' || !('graphQLErrors' in exception)) {
        return false;
    }

    const { graphQLErrors } = exception;
    if (!Array.isArray(graphQLErrors) || graphQLErrors.length === 0) {
        return false;
    }

    return graphQLErrors.every(error => !isDeveloperGraphQLError(error));
}

// A urql CombinedError whose request never received a response is a transport-level failure: the
// fetch rejected before a response arrived (connectivity dropped, or the tab was backgrounded
// mid-fetch on mobile Safari — every concurrent request fails together with status 0, and
// "[Network] Load failed" is Safari's generic aborted-fetch message). The user is already notified
// via graphqlErrorHandler; async component setup then re-throws to halt rendering, which Sentry
// captures as a crash. A network drop is never an actionable frontend bug. PREPBUSINESS-B13
//
// The empty `response` is the discriminator: urql only leaves it undefined when fetch() itself
// rejected (urql-core fetchOperation). A CombinedError that DID receive a response — e.g. a 500
// from the GraphQL endpoint, surfaced as a networkError of `new Error(statusText)` — keeps a
// `response` and stays visible, so genuine server/endpoint regressions still reach Sentry.
function isGraphQLNetworkError(exception: unknown): boolean {
    if (exception == null || typeof exception !== 'object'
        || !('graphQLErrors' in exception) || !('networkError' in exception) || !('response' in exception)) {
        return false;
    }

    const { graphQLErrors, networkError, response } = exception;

    return networkError != null
        && response == null
        && Array.isArray(graphQLErrors)
        && graphQLErrors.length === 0;
}

// The opaque TypeError a rejected fetch() produces: "Failed to fetch" (Chrome) or "Load failed"
// (Safari). Sentry's own fetch instrumentation (@sentry/core) rewrites this message to append the
// request's host whenever fetch() was given an absolute URL, in every browser, e.g.
// "Failed to fetch (safesearchinc.com)"; relative URLs keep the bare message. PREPBUSINESS-BCT
const fetchFailedMessage = /^(Failed to fetch|Load failed)( \(.+\))?$/;

function shouldFilterEvent(event: Event, hint: EventHint): boolean {
    // Filter out CustomEvent unhandled rejections from browser extensions
    // instanceof check fails for cross-realm CustomEvents, so we check constructor name instead
    // PREPBUSINESS-71X
    if (hint.originalException?.constructor?.name === 'CustomEvent') {
        return true;
    }

    // Filter out expected GraphQL business errors (e.g. billing/permission gates the backend
    // returns as a 200 with a graphQLErrors array, like "You must add a payment method before
    // using the portal"). The user is already notified via graphqlErrorHandler; async component
    // setup re-throws to halt rendering, which Sentry then captures as a crash. A CombinedError
    // carrying a backend-authored graphQLErrors message is never an actionable frontend bug.
    // PREPBUSINESS-ABM
    if (isGraphQLBusinessError(hint.originalException)) {
        return true;
    }

    // Filter out transient GraphQL network failures (see isGraphQLNetworkError). PREPBUSINESS-B13
    if (isGraphQLNetworkError(hint.originalException)) {
        return true;
    }

    const firstException = event.exception?.values?.[0];

    // Ably is bundled so denyUrls can't filter it. All Ably-internal unhandled rejections
    // have no stacktrace; our application code always produces one. Nothing with a null
    // stacktrace on an unhandledrejection is actionable. PREPBUSINESS-AD0, PREPBUSINESS-AMA
    if (
        firstException?.mechanism?.type === 'onunhandledrejection'
        && !firstException.stacktrace
    ) {
        return true;
    }

    // Filter out errors from third-party browser extensions that eval scripts against
    // our API. These have a single eval frame with no filename. PREPBUSINESS-ADC
    const frames = firstException?.stacktrace?.frames;
    if (
        firstException?.mechanism?.type === 'onunhandledrejection'
        && frames?.length === 1
        && frames[0]?.function === 'eval'
        && (frames[0].filename == null || frames[0].filename === '')
    ) {
        return true;
    }

    // Filter out injected third-party script (Google Translate, browser extensions) whose
    // unhandled rejections Sentry maps onto the HTML document — the frames point at index.html's
    // inline <style> block rather than a real bundle. Our code only ever ships in /assets/*.js,
    // so an unhandledrejection with no JS-asset frame is never actionable. PREPBUSINESS-B0D
    if (
        firstException?.mechanism?.type === 'onunhandledrejection'
        && frames != null
        && frames.length > 0
        && frames.every(frame => frame.filename == null || !/\.js($|\?)/.test(frame.filename))
    ) {
        return true;
    }

    // Filter out crashes caused by browser/extension page translation (Google Translate on
    // Chrome, the built-in Safari translator) rewriting text nodes inside Vue-rendered DOM.
    // Translation detaches the DOM nodes Vue caches, so Vue's next patch/unmount dereferences a
    // null el and throws from deep inside the Vue runtime. These carry the Vue error mechanism
    // and a message referencing a Vue-internal operation on a null value: subTree and emitsOptions
    // (component internals) and the beforeUnmount hook (bum) are names application code never
    // references. parentNode and insertBefore ARE real DOM members, so in principle an app-level
    // null-deref through them could be caught here too — but we only ever hand Vue-managed nodes to
    // Vue, and our one direct DOM manipulation of such a node (intercomWidget) is optional-chained,
    // so suppressing them is a deliberate, safe trade-off. Chrome phrases these as "reading 'x'",
    // Safari as "evaluating 'obj.x'", so both wordings must be matched.
    //
    // The null-instance destructure inside Vue's unmount path is caught the same way: Chrome names
    // the property ("destructure property 'bum'"), but Safari only emits the generic "Right side of
    // assignment cannot be destructured", which we also match. That Safari wording is a slightly
    // broader net (it names no property), accepted because the same crash reports on Chrome with the
    // Vue-specific 'bum' identifier and any genuine null-destructure bug would surface there too.
    //
    // beforeSend runs on the RAW client-side stack, whose frames point at the minified bundle
    // chunks we ship under /assets/*.js — the readable @vue/runtime-* paths only appear after
    // Sentry applies uploaded source maps server-side, so we cannot key on them here. We require
    // every frame to originate from our own bundle (or, defensively, the Vue runtime); native
    // built-ins (e.g. Array methods) are allowed through — they carry no filename on Chrome, while
    // Safari reports them as "[native code]" (e.g. the reactivity effect's run/runIfDirty), which
    // previously let every Safari crash slip past this guard (PREPBUSINESS-BH9, PREPBUSINESS-BHA). This excludes
    // injected extension/translation scripts while confirming the crash unwound entirely through
    // app code. This is unfixable from app code without disabling translation, which real users
    // rely on, and is an open upstream Vue issue.
    // PREPBUSINESS-B0M, PREPBUSINESS-B0N, PREPBUSINESS-B0Q, PREPBUSINESS-B0S, PREPBUSINESS-B0T,
    // PREPBUSINESS-B0P, PREPBUSINESS-B15, PREPBUSINESS-BH9, PREPBUSINESS-BHA
    if (
        firstException?.mechanism?.type === 'vue'
        && /(reading|evaluating) '[^']*(parentNode|subTree|insertBefore|emitsOptions)'|destructure property 'bum'|Right side of assignment cannot be destructured/.test(firstException.value ?? '')
        && frames != null
        && frames.length > 0
        && frames.every(frame => frame.filename == null || /(\/assets\/.+\.js)|(@vue\/(runtime|reactivity))|^\[native code\]$/.test(frame.filename))
    ) {
        return true;
    }

    // Filter out onerror-mechanism exceptions whose entire stack resolves outside our own
    // bundle. We only ship application code as /assets/*.js, so a frame whose filename actually
    // resolved to something else (e.g. the page's own document URL) proves the throwing code ran
    // inline in the document, not from our bundle — a third-party extension/injected script. A
    // frame with no filename at all is NOT that proof — it's simply unresolved, which can happen
    // for a genuine app frame too (e.g. a dropped source map), so it must not satisfy this check.
    //
    // Originally scoped to the "Identifier '<name>' has already been declared" SyntaxError a
    // browser extension's content script throws when re-injected into the page more than once
    // (e.g. on back/forward navigation within this SPA) and its top-level `let`/`const`
    // redeclares over the first injection (PREPBUSINESS-B5Q). Broadened after the same
    // page-URL-only frame signature recurred with an unrelated, near-empty message ("fa") that
    // carried no other diagnosable content — the frame shape alone is sufficient proof it isn't
    // ours, regardless of the exception's type/message. PREPBUSINESS-B5Q, PREPBUSINESS-B6E
    if (
        firstException?.mechanism?.type === 'onerror'
        && frames != null
        && frames.length > 0
        && frames.every(frame => frame.filename != null && frame.filename !== '' && !/\.js($|\?)/.test(frame.filename))
    ) {
        return true;
    }

    // Filter out stack-overflow crashes thrown from an injected third-party page-translation
    // script (Google Translate). Translate's recursive DOM walker retranslates the
    // Vue-rendered DOM and blows the call stack (RangeError), thrown from a setTimeout it
    // schedules — so the mechanism is 'instrument', not the 'vue'/'onunhandledrejection' the
    // filters above key on. As with those, every frame resolves to the HTML document (the
    // route path, e.g. "/error") rather than a real /assets/*.js bundle; our code only ever
    // ships in /assets/*.js, so a "Maximum call stack size exceeded" carrying no JS-asset
    // frame did not originate in app code and is unfixable without disabling translation,
    // which real users rely on. A genuine app infinite-recursion always surfaces at least one
    // /assets/*.js frame. PREPBUSINESS-B19, PREPBUSINESS-B1D, PREPBUSINESS-B1F
    if (
        firstException?.type === 'RangeError'
        && /Maximum call stack size exceeded/.test(firstException.value ?? '')
        && frames != null
        && frames.length > 0
        && frames.every(frame => frame.filename == null || !/\.js($|\?)/.test(frame.filename))
    ) {
        return true;
    }

    // Filter out PostHog session recording (rrweb) crashing while tearing itself down inside a
    // deferred setTimeout: LazyLoadedSessionRecording._teardown stops the active recording
    // strategy, whose rrweb-record safe-invoke wrapper (`ue(t){try{t()}...}`) calls a callback
    // ref that an earlier teardown already cleared, so invoking it throws "<param> is not a
    // function". The message alone (a single minified parameter name) is too generic to match on
    // its own — it could coincidentally match a real app bug — so this also requires the
    // setTimeout instrumentation mechanism Sentry auto-attaches, and a stack that resolves
    // entirely inside posthog-js/rrweb/@sentry-browser vendor code, never our own /assets bundle.
    // Internal to posthog-js, unfixable from app code. PREPBUSINESS-B82
    if (
        firstException?.type === 'TypeError'
        && /^\w+ is not a function$/.test(firstException.value ?? '')
        && firstException.mechanism?.type === 'instrument'
        && firstException.mechanism.data?.function === 'setTimeout'
        && frames != null
        && frames.length > 0
        && frames.every(frame => frame.filename != null
            && /(posthog-js)|(rrweb\/record)|(@sentry\/browser)|(lazy-loaded-session-recorder)/.test(frame.filename))
    ) {
        return true;
    }

    // Filter out PostHog's network-capture plugin surfacing a fetch() rejection as "Failed to
    // fetch" (Chrome) or "Load failed" (Safari). PostHog patches the global window.fetch, so
    // every fetch() call on the page — including our own GraphQL transport, /broadcasting/auth,
    // and the QZ Tray digital-certificate signing request — is routed through its wrapper and
    // could, in principle, produce this exact message on a genuine network failure. The message
    // alone is therefore not a safe discriminator: this previously lived as a plain ignoreErrors
    // string, which would have silently dropped a real outage in any of those paths too. Instead
    // require the entire stack to resolve inside posthog-js's own code — a call that passed
    // through our app code first carries at least one /assets bundle frame beneath the wrapper
    // and fails this check, keeping a genuine failure from one of the paths above visible in
    // Sentry. PREPBUSINESS-A40, PREPBUSINESS-B8W
    if (
        firstException?.type === 'TypeError'
        && fetchFailedMessage.test(firstException.value ?? '')
        && frames != null
        && frames.length > 0
        && frames.every(frame => frame.filename != null && /posthog-js/.test(frame.filename))
    ) {
        return true;
    }

    // Filter out "Failed to fetch" rejections caused by a browser extension that has
    // monkey-patched window.fetch (e.g. an ad blocker intercepting/blocking the request itself).
    // Unlike the PostHog case above, the extension sits *between* our call site and the network,
    // so a real request we made - Ably's requestTokenFn calling our own /broadcasting/auth
    // fetch(), in the reported case - still carries our app/library frames beneath the
    // extension's wrapper; requiring the whole stack to resolve outside our code would miss it.
    // The chrome-extension:// frame is itself the proof: it names a real, resolvable script that
    // we never ship, so its presence anywhere in the stack means a third party's own fetch
    // wrapper produced the rejection, not our request logic. PREPBUSINESS-B95
    //
    // Known accepted tradeoff: an extension that patches window.fetch but delegates to the real
    // implementation produces an identical stack shape whether it actively blocked the request or
    // merely sat in front of a genuine DNS/TLS/offline failure - the Fetch spec deliberately
    // reports both as the same opaque TypeError with no further detail, so there is no signal left
    // to tell them apart. Requiring the *entire* stack to resolve inside the extension (as the
    // PostHog filter does) would close that gap, but it would also reject the exact case this
    // filter exists for, since Ably's bundled frames are legitimately on the stack beneath the
    // wrapper for every real request. We accept the small risk of suppressing a genuine failure
    // that happens to coincide with an extension's fetch patch, in exchange for actually catching
    // the extension noise this was added to fix.
    if (
        firstException?.type === 'TypeError'
        && fetchFailedMessage.test(firstException.value ?? '')
        && frames != null
        && frames.some(frame => frame.filename?.startsWith('chrome-extension://') === true)
    ) {
        return true;
    }

    // Filter out "Failed to fetch" rejections from a browser extension's injected script making
    // its own requests (adware calling safesearchinc.com, in the reported case). A frame whose
    // filename is "<anonymous>" WITH a line number comes from a script that has no URL at all,
    // such as code an extension injects via chrome.scripting.executeScript. (eval / new Function
    // code does not look like this by the time it reaches beforeSend: Sentry's stack parser
    // reattributes those frames to the URL of the script that called eval.) Native built-ins also
    // show as "<anonymous>", but without a position. Every script of ours or a vendor's is loaded
    // from a URL, so a positioned "<anonymous>" frame is never our code. Sentry's own fetch instrumentation (in our /assets bundle) wraps
    // the injected call, so the "no JS-asset frame" filter above can't catch this.
    // PREPBUSINESS-BCT
    //
    // Known accepted tradeoff, as with the chrome-extension:// filter above: this matches an
    // "<anonymous>" frame anywhere on the stack, so if an injected script wraps window.fetch and
    // delegates to the real one, a genuine failure of one of our own requests that passes through
    // that wrapper is dropped too.
    if (
        firstException?.type === 'TypeError'
        && fetchFailedMessage.test(firstException.value ?? '')
        && frames != null
        && frames.some(frame => frame.filename === '<anonymous>' && frame.lineno != null)
    ) {
        return true;
    }

    // Headless UI's Portal (used internally by every Dialog/Menu/Listbox/Combobox/Popover)
    // lazily creates a single shared `#headlessui-portal-root` div and appends it to
    // document.body the first time any Portal-based component mounts, then removes it again
    // once the last portal using it unmounts. Reaching that create+append requires a Vue
    // reactive effect that was already scheduled (Vue's flushJobs) — e.g. a pending
    // websocket-driven refresh or polling callback — to still be flushing as the browser
    // tears down the document (navigating away, or the tab being discarded and reloaded).
    // At that instant document.body is momentarily null, which nothing in our reactive graph
    // can detect or guard against. Scoped to the whole stack resolving inside the headlessui
    // vendor chunk (which bundles the relevant Vue runtime-core internals alongside it) so a
    // genuine null-appendChild bug in our own app code (e.g. the CSV/file download helpers
    // that also call document.body.appendChild) still reaches Sentry. PREPBUSINESS-B93
    if (
        firstException?.mechanism?.type === 'vue'
        && firstException.type === 'TypeError'
        && firstException.value === 'Cannot read properties of null (reading \'appendChild\')'
        && frames != null
        && frames.length > 0
        && frames.every(frame => frame.filename != null && /\/assets\/headlessui-/.test(frame.filename))
    ) {
        return true;
    }

    return false;
}

// @sentry/vue does not re-export the SpanJSON type that beforeSendSpan receives.
type SpanJSON = Parameters<NonNullable<BrowserOptions['beforeSendSpan']>>[0];

// The consent page URL, the login URL returning to it and the consent read request all carry the
// pending authorization request ID. Navigation and XHR breadcrumbs, request spans and the page URL
// Sentry attaches to every event would otherwise report it.
function redactPlatformOauthBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
    return {
        ...breadcrumb,
        message: breadcrumb.message == null ? breadcrumb.message : redactPlatformOauthSecrets(breadcrumb.message),
        data: breadcrumb.data == null ? breadcrumb.data : redactPlatformOauthValues(breadcrumb.data),
    };
}

function redactPlatformOauthSpan(span: SpanJSON): SpanJSON {
    return {
        ...span,
        description: span.description == null ? span.description : redactPlatformOauthSecrets(span.description),
        data: redactPlatformOauthValues(span.data),
    };
}

function redactPlatformOauthEvent(event: Event): Event {
    if (event.request != null) {
        event.request = {
            ...event.request,
            url: event.request.url == null ? event.request.url : redactPlatformOauthSecrets(event.request.url),
            headers: event.request.headers == null ? event.request.headers : redactPlatformOauthValues(event.request.headers),
        };
    }

    if (event.transaction != null) {
        event.transaction = redactPlatformOauthSecrets(event.transaction);
    }

    if (event.contexts?.feedback != null) {
        event.contexts.feedback = redactPlatformOauthValues(event.contexts.feedback);
    }

    return event;
}

// A burst of 500s fires showFeedback repeatedly; without this the widget stacks a
// fresh dialog into its shadow root for every one of them.
let feedbackFormOpen = false;

export default function useSentry(): { initSentry: (app: App, dns: string, release: string) => void; showFeedback: () => void; } {
    function initSentry(app: App, dns: string, release: string): void {
        if (dns === '') {
            return;
        }

        const { currentUserOptional, currentMerchantOptional, currentServiceProviderOptional } = useNarrative();

        Sentry.init({
            app,
            dsn: dns,
            release,
            integrations: [
                Sentry.browserTracingIntegration(),
                // The legacy showReportDialog injects a remote script that appends an
                // un-nonced <style>, which style-src-elem blocks and offers no way to
                // authorize. This widget applies styleNonce to every <style> it creates,
                // including the one inside its shadow root.
                Sentry.feedbackIntegration({
                    autoInject: false,
                    enableScreenshot: false,
                    styleNonce: getCspNonce(),
                    useSentryUser: { email: 'email', name: 'name' },
                    formTitle: 'It looks like we\'re having issues.',
                    messageLabel: 'What happened?',
                    messagePlaceholder: 'Our team has been notified. If you\'d like to help, tell us what happened below.',
                    submitButtonLabel: 'Submit Crash Report',
                }),
            ],
            tracesSampleRate: 0.01,
            initialScope: {
                tags: {
                    repository: 'conveyr-ui',
                },
            },
            beforeBreadcrumb: redactPlatformOauthBreadcrumb,
            beforeSendSpan: redactPlatformOauthSpan,
            beforeSend(event, hint) {
                if (shouldFilterEvent(event, hint)) {
                    return null;
                }

                // Fingerprint Axios 500 errors by endpoint so each API route gets its own Sentry issue
                // rather than all 500s being grouped into one issue by stack trace
                if (isAxiosError(hint.originalException) && hint.originalException.response?.status === 500) {
                    const url = hint.originalException.config?.url ?? '';
                    event.fingerprint = ['axios-500', url.replace(/\/\d+/g, '/{id}')];
                }

                // Fingerprint "out of memory" crashes by page path. As PREPBUSINESS-B87 found,
                // these carry no app-code stack frames, so Sentry's default stack-based grouping
                // would otherwise merge every future occurrence - on any page, for any reason -
                // into this same single "one-off, already investigated" issue, burying a real
                // recurrence or a page-specific cluster instead of surfacing it as new.
                if (event.exception?.values?.[0]?.value === 'out of memory') {
                    event.fingerprint = ['out-of-memory', window.location.pathname];
                }

                return event;
            },
            denyUrls: [
                /js\.chargebee\.com/, // Chargebee SDK internal iframe errors (PREPBUSINESS-9S2)
                /js\.stripe\.com/, // Stripe SDK internal iframe race conditions (PREPBUSINESS-AH2)
                /Honey\.safariextension/, // PayPal Honey Safari extension internal errors (PREPBUSINESS-AKT)
                /client\.crisp\.chat/, // Crisp chat widget internal errors not actionable from app code (PREPBUSINESS-AKZ)
            ],
            ignoreErrors: [
                'Request aborted',
                'Printing cancelled',
                'Unable to establish connection with QZ',
                /Connection attempt denied by Local Network Access restrictions/, // Chrome 147+ LNA permission prompt denial (https://qz.io/docs/lna), a client browser-permission state, not an app bug
                /qz-tray/,
                'NullPointerException', // QZ Tray Java backend error propagated via WebSocket message (PREPBUSINESS-4FW)
                'Network Error',
                'timeout exceeded',
                'function Error() { [native code] }',
                'Connection closed before response received',
                /Cannot read properties of null \(reading '(sendData|established)'\)/,
                /null is not an object \(evaluating '.*\.websocket\.connection\./, // Safari: QZ Tray race condition when connection closes mid-handshake (PREPBUSINESS-A4H, PREPBUSINESS-AB3)
                /\.websocket\.connection\.(sendData|established) is not a function/, // Safari undefined-property variant (PREPBUSINESS-A4H)
                /can't access property "(sendData|established)", .* is null/, // Firefox: QZ Tray race condition when connection closes mid-handshake (PREPBUSINESS-AK4)
                /Request failed with status code 4\d\d/, // This will match any 4xx status code
                /Request failed with status code 50[234]/, // Matches 502, 503, 504 (handled in axios.ts)
                /Request failed with status code 524/, // Matches 524 Cloudflare timeout
                'canceled',
                /Timeout waiting for/,
                /AbortError/, // Matches PostHog AbortErrors
                /enabledFeatures is undefined/, // PostHog site app error
                'Conversation does not exist', // Crisp chat widget error (PREPBUSINESS-A18)
                'Unable to preload CSS', // Vite CSS cache mismatch, auto-reloaded (PREPBUSINESS-A1Y)
                /Couldn't resolve component/, // Stale chunk after deploy, auto-reloaded by router onError (PREPBUSINESS-AKH)
                /Blocked a frame with origin .* from accessing a cross-origin frame/, // Sentry instrumentation hitting third-party iframes (PREPBUSINESS-9TE)
                'Called on script loaded before session recording is available', // PostHog internal rrweb race condition, unfixable from app code (PREPBUSINESS-A36)
                'Failed to execute \'getComputedStyle\' on \'Window\': parameter 1 is not of type \'Element\'.', // floating-ui race condition on BaseCombobox unmount (PREPBUSINESS-A49)
                'The object is in an invalid state.', // Safari InvalidStateError from QZ Tray WebSocket.send() in detached promise chain (PREPBUSINESS-A8H)
                /Failed to construct 'WebSocket'/, // window.WebSocket replaced by a broken (non-newable) wrapper from a browser extension/automation env; Ably constructs with `new` correctly and already falls back, the setTimeout connectivity probe just leaks it to Sentry (PREPBUSINESS-B21)
                // Same broken-WebSocket-wrapper category as B21, but the replacement constructor throws
                // its own Error instead of a TypeError. Native browsers never throw this wording
                // synchronously (Chrome reports connection failures as an async error Event, and quotes
                // the URL). Ably already catches the throw in WebSocketTransport.connect and falls back
                // to its other hosts; the wrapper re-reports it to window.onerror itself (PREPBUSINESS-BCW)
                /WebSocket connection to \S+ failed: transport not supported/,
                'Blob is not a constructor', // global.Blob replaced by a broken (non-newable) wrapper from a browser extension/automation env; canvas-confetti already catches this in its own try/catch, so the throw reaching Sentry originates outside our code (PREPBUSINESS-B2F)
                /bufferBelongsToIframe/, // PostHog rrweb-record race condition during iframe cleanup on page hide (PREPBUSINESS-AFX)
                // Android WebView's Java bridge (addJavascriptInterface) reflection layer, injected by a
                // third-party in-app browser/extension, throwing on a method call it doesn't implement.
                // Sentry groups every variant under one issue (same anonymous bridge stack) but the method
                // name and failure wording vary per host app — "Error invoking postEvent: Method not found"
                // and "Error invoking batch: Java bridge method invocation error" both recurred under
                // PREPBUSINESS-ANP after the exact-string match only covered the first. No app dependency
                // calls a "postEvent" or "batch" bridge method, so match the whole family by prefix instead.
                /^Error invoking \w+: /,
                'NetworkInterceptionError', // User's network (captive portal/proxy) intercepted API requests; user is already notified via popup (PREPBUSINESS-ANS)
                'Failed to load inbound shipment', // Shipment missing/inaccessible or user navigated away mid-query; handled in InboundShipmentView with a notification + redirect, the throw only halts async setup (PREPBUSINESS-B0H)
                'Failed to load Stripe.js', // Stripe's external js.stripe.com script blocked by an ad blocker/extension or network failure; loadStripe rejects from the bundled loader (@stripe/stripe-js) so denyUrls can't catch it, unfixable from app code (PREPBUSINESS-B0V)
                'Cannot redefine property: adoptedStyleSheets', // PostHog session recording (rrweb) re-patching document.adoptedStyleSheets when its mutation observers reset twice on the same document; the first patch installs a non-configurable own property, so the second Object.defineProperty call throws. Internal to posthog-js, unfixable from app code (PREPBUSINESS-B7R)
                // A third-party page-automation tool/browser extension polling the live page with a
                // Playwright/testing-library-style locator (e.g. `text=Add Items to Shipment`) handed
                // straight to the native document.querySelector, which has no concept of that `engine=`
                // prefix syntax and throws a SyntaxError. Our own code never builds selector strings this
                // way (grep confirms every querySelector call in the app uses a static CSS selector), so
                // this can never be a genuine app bug. PREPBUSINESS-B99
                /Failed to execute 'querySelector(All)?' on '(Document|Element)': '(text|xpath|css|role)=.*' is not a valid selector\./,
            ],
        });

        // An event processor rather than beforeSend, which never sees transactions or feedback.
        Sentry.addEventProcessor(redactPlatformOauthEvent);

        // Sentry initialises at app boot, before authentication resolves, so identity and
        // tenant context are attached reactively as the narrative store hydrates.
        watch(currentUserOptional, (user) => {
            Sentry.setUser(user != null
                ? { id: user.id, email: user.email, name: user.name }
                : null);
        }, { immediate: true });

        watch(currentMerchantOptional, (merchant) => {
            Sentry.setContext('merchant', merchant != null
                ? { id: merchant.id, name: merchant.name }
                : null);
        }, { immediate: true });

        watch(currentServiceProviderOptional, (serviceProvider) => {
            Sentry.setContext('service_provider', serviceProvider != null
                ? { id: serviceProvider.id, name: serviceProvider.name }
                : null);
        }, { immediate: true });
    }

    const showFeedback = (): void => {
        // Read the originating event before the delay — a later error would otherwise
        // move lastEventId and attach this report to the wrong event.
        const associatedEventId = Sentry.getCurrentScope().lastEventId();

        setTimeout(() => {
            const feedback = Sentry.getFeedback();

            if (feedback == null || feedbackFormOpen) {
                return;
            }

            feedbackFormOpen = true;

            feedback.createForm({
                // The widget builds its own send payload and exposes no hook for
                // associatedEventId, so the originating event is carried as a tag.
                tags: associatedEventId == null ? undefined : { associated_event_id: associatedEventId },
                onFormClose: () => {
                    feedbackFormOpen = false;
                },
                onFormSubmitted: () => {
                    feedbackFormOpen = false;
                },
            }).then((form) => {
                form.appendToDom();
                form.open();
            }).catch((error: unknown) => {
                // Releasing the guard matters more than the report: latching it here would
                // disable crash feedback for the rest of the session. Rethrowing would only
                // reach Sentry as a context-free unhandled rejection.
                feedbackFormOpen = false;
                Sentry.captureException(error);
            });
        }, 2000);
    };

    return {
        initSentry,
        showFeedback,
    };
}

export const showSentryFeedback = (): void => {
    if (useNarrative().sentryPublicDsn.value == null || useNarrative().sentryPublicDsn.value === '') {
        return;
    }

    useSentry().showFeedback();
};
